/**
 * Get Started Checklist Types
 *
 * Defines the step identifiers and interfaces for the progressive
 * onboarding checklist widget that tracks real user actions.
 */

import type { CompanyPayment } from '@scio3/shared-types';

/** Unique identifier for each checklist step */
export enum StepId {
  GET_SCREEN_SETUP = 'getScreenSetup',
  PAIR_SCREEN = 'pairScreen',
  CREATE_CONTENT = 'createContent',
  CREATE_PLAYLIST = 'createPlaylist',
}

/** Direction group with its own completion flag */
export interface DirectionGroup {
  /** Backend flag key to check for this group's completion */
  settingKey: keyof CompanyPayment;
  /** i18n keys for direction bullet points */
  directions: string[];
}

/** Definition of a single checklist step */
export interface ChecklistStep {
  id: StepId;
  /** i18n key for step title */
  titleKey: string;
  /** Route to navigate when step action is clicked */
  route: string;
  /** Whether this step is required to complete the checklist */
  required: boolean;
  /** Direction groups with separate completion tracking */
  directionGroups?: DirectionGroup[];
  /** Simple directions (all share step completion) */
  directions?: string[];
}

/** Runtime state of a step (definition + completion status) */
export interface StepState extends ChecklistStep {
  completed: boolean;
}

/** Overall checklist state */
export interface ChecklistState {
  /** All steps are completed (required ones at minimum) */
  isComplete: boolean;
  /** Checklist widget should be visible */
  isVisible: boolean;
  /** Steps with their completion status */
  steps: StepState[];
  /** Count of completed steps */
  completedCount: number;
  /** Total number of steps */
  totalSteps: number;
  /** Progress percentage (0-100) */
  progressPercent: number;
}
